import type { ThunkDispatch } from "@reduxjs/toolkit";

import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import { RestaurantTermsAcceptanceModal } from "@dashboard/components/RestaurantModal/RestaurantTermsAcceptanceModal";
import { fetchRestaurantBranding } from "@dashboard/store/actions/brandingActions";
import type { BrandingReducerAction } from "@dashboard/store/reducers/brandingReducer";
import type { ReduxState, ThunkExtraArgument } from "@dashboard/types";

import { NavigableErrorBoundary } from "../components/errors/ErrorBoundaries";
import { AxiosRestaurantAuthZProvider } from "../context";
import { useSnackbar } from "../hooks";
import { onRestaurantSelected } from "../monitoring";

/**
 * Once the user is authenticated, this layout provides the current Restaurant
 * and contextual information about the restaurant to all pages. This includes
 * currency, price formattting, country, name, restaurant uuid, etc.
 *
 * This layout expects the initial restaurant list is already loaded.
 *
 * Selection of the correct restaurant is managed by a modal in the header.
 */
const RestaurantManagementLayoutComponent = ({
  fetchBranding,
}: {
  fetchBranding: (
    restaurantUuid: string,
    onError: (error: Error) => void
  ) => void;
}) => {
  const { accessibleRestaurants, selectedRestaurant } = useSelector(
    (state: ReduxState) => state.restaurant
  );
  const snackbar = useSnackbar();

  // The following hooks control the state of the app based on the currently selected restaurant.
  // Their data is used by more than one component, hence the data fetching is done here.

  useEffect(() => {
    if (!selectedRestaurant) {
      return;
    }
    // Allow analytics to caputure the first restaurant that
    // is being loaded.
    onRestaurantSelected(selectedRestaurant);
    // Branding is used for the sidebar logo.
    fetchBranding(selectedRestaurant.uuid, () => {
      snackbar.error("Some data failed to load. Please refresh the page.");
    });
  }, [selectedRestaurant?.uuid]);

  if (accessibleRestaurants.length === 0 || !selectedRestaurant) {
    // if at least 1 restaurant exists, that is chosen as the selected restaurant
    // by default in the restaurant reducer
    return <Navigate to="/restaurants/create" />;
  }

  return (
    <NavigableErrorBoundary
      // Ensure that the error boundary is reset when the restaurant changes.
      // This allows for resetting authZ provider state as well.
      key={selectedRestaurant.uuid}
    >
      {/* This adds restaurant uuid to axios calls. */}
      <AxiosRestaurantAuthZProvider>
        <Outlet key={selectedRestaurant.uuid} />
        <RestaurantTermsAcceptanceModal />
      </AxiosRestaurantAuthZProvider>
    </NavigableErrorBoundary>
  );
};

export const RestaurantManagementLayout = connect(
  null,
  (
    dispatch: ThunkDispatch<
      ReduxState,
      ThunkExtraArgument,
      BrandingReducerAction
    >
  ) => ({
    fetchBranding: (restaurantUuid: string, onError: (error: Error) => void) =>
      dispatch(fetchRestaurantBranding(restaurantUuid, onError)),
  })
)(RestaurantManagementLayoutComponent);
